import {tokens} from '@pleo-io/telescope'
import React from 'react'
import styled from 'styled-components'

import FeatureBlock from '@/components/feature-block'
import RotatingElements from '@/components/rotating-elements'
import SimpleSignupForm from '@/components/simple-signup-form'
import {WidgetTrustpilot} from '@/components/widget-trustpilot'
import {HeroFragment} from '@/graphql-types'
import useParams from '@/hooks/use-params'
import Image from '@helios/shared/components/image'
import useMediaQuery from '@helios/shared/hooks/use-media-query'
import {Section} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import {getExperiments} from '@helios/shared/utils/get-experiments'
import mq from '@helios/shared/utils/media-query'

interface Props {
    slice: HeroFragment
}

const mobileBreakpoint = 'xs'

export const Hero: React.FC<Props> = ({slice}) => {
    const content = slice.primary
    const isSmallScreen = useMediaQuery.max(mobileBreakpoint)
    const experiments = getExperiments()
    const params = useParams()
    const [showTrustpilot, setShowTrustpilot] = React.useState(false)

    React.useEffect(() => {
        if (params.has('show_trustpilot')) {
            localStorage.setItem('showTrustpilot', 'true')
        }
        setShowTrustpilot(localStorage.getItem('showTrustpilot') === 'true')
    }, [params])

    const rotatingSignupReasons = [
        content.signup_reason_1,
        content.signup_reason_2,
        content.signup_reason_3,
    ].filter((reason) => !!reason)

    const video = undefined

    return (
        <Section padding="small">
            <FeatureBlock
                videoId={content.wistiaVideoId}
                inlineVideo={video}
                image={content.image}
                title={content.title}
                body={content.lead_text.raw}
                mirrored={!!content.inverse}
                titleSize="xl"
                bodyProps={{size: 'sm'}}
                headingLevel="h1"
            >
                <SignupArea>
                    <SimpleSignupForm
                        {...content.predefined_signup_banner.document.data}
                        withBorder
                        placeholder={
                            content.predefined_signup_banner.document.data.email_input_placeholder
                        }
                        submitText={content.predefined_signup_banner.document.data.submit_text}
                        errorText={
                            content.predefined_signup_banner.document.data.email_input_error_text
                        }
                    />

                    {content.rotating_signup_reasons && rotatingSignupReasons.length > 0 && (
                        <RotatingElements isInView={true}>
                            {rotatingSignupReasons.map((reason, i) => (
                                <Reason key={i}>
                                    <Image image={reason.document.data.icon} />

                                    <ReasonTextWrapper>
                                        <Text variant="label-base-medium">
                                            {reason.document.data.primary}
                                        </Text>
                                        {reason.document.data.secondary && (
                                            <Text size="xxs" font="secondary">
                                                {!isSmallScreen && ' ・ '}
                                                {reason.document.data.secondary}
                                            </Text>
                                        )}
                                    </ReasonTextWrapper>
                                </Reason>
                            ))}
                        </RotatingElements>
                    )}
                </SignupArea>
                {experiments.trustpilotWidget === 'show' && showTrustpilot && <WidgetTrustpilot />}
            </FeatureBlock>
        </Section>
    )
}

export const Reason = styled.div`
    display: grid;
    grid-template-columns: 28px auto;
    gap: ${tokens.spacing8};
    justify-content: center;
    align-items: center;

    ${mq.min(mobileBreakpoint)} {
        justify-content: start;
    }
`

export const ReasonTextWrapper = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    justify-content: start;

    ${mq.min(mobileBreakpoint)} {
        grid-template-columns: auto auto;
    }
`

const SignupArea = styled.div`
    display: grid;
    gap: ${tokens.spacing12};
    width: 100%;
`
